
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useCollections } from "@/contexts/collections";
import { ContentItem } from "@/components/dashboard/content-card/types";
import { 
  fetchCollectionDetailsFromDb,
  fetchContentFromCollection,
  fetchAllRelatedContent,
  fetchItemCollectionDetails
} from "./index";
import { supabase } from "@/integrations/supabase/client";

interface UseRelatedContentProps {
  collectionId?: string;
  currentContentId?: string;
  sourceType?: "collection" | "content";
}

export const useRelatedContent = (
  idOrProps: string | UseRelatedContentProps,
  sourceType?: "collection" | "content"
) => {
  let collectionId: string | undefined;
  let currentContentId: string | undefined;
  let resolvedSourceType: "collection" | "content" = "content";

  if (typeof idOrProps === 'string') {
    if (sourceType === "collection") {
      collectionId = idOrProps;
      resolvedSourceType = "collection";
    } else {
      currentContentId = idOrProps;
      resolvedSourceType = "content";
    }
  } else {
    collectionId = idOrProps.collectionId;
    currentContentId = idOrProps.currentContentId;
    resolvedSourceType = idOrProps.sourceType || "content";
  }

  const { user } = useAuth();
  const { getCollectionById } = useCollections();
  const [collectionColor, setCollectionColor] = useState<string>("#6B7789");
  const [collectionName, setCollectionName] = useState<string>("Related Content");

  // If currentContentId is provided but no collectionId, we need to fetch the collection ID
  const { data: contentCollectionInfo } = useQuery({
    queryKey: ["contentCollectionInfo", currentContentId],
    queryFn: async () => {
      if (!currentContentId || !user || collectionId) return null;
      
      try {
        // Get the collection ID for the current content
        const { data, error } = await supabase
          .from('content_collections')
          .select('collection_id')
          .eq('content_id', currentContentId)
          .maybeSingle();
          
        if (error || !data) {
          return null;
        }
        
        return data;
      } catch (error) {
        console.error("Error in contentCollectionInfo:", error);
        return null;
      }
    },
    enabled: !!currentContentId && !!user && !collectionId,
  });
  
  // Extract collection ID from content's collection info
  const derivedCollectionId = contentCollectionInfo?.collection_id;
  
  // Use either explicit collectionId or derived from content's collection
  const effectiveCollectionId = collectionId || derivedCollectionId;

  const { data: collectionDetails } = useQuery({
    queryKey: ["collectionDetails", effectiveCollectionId],
    queryFn: async () => {
      if (!effectiveCollectionId || !user) return null;

      try {
        const collectionFromContext = getCollectionById(effectiveCollectionId);
        
        if (collectionFromContext) {
          console.log(`[COLOR DEBUG] Found collection in context: ${effectiveCollectionId}, color: ${collectionFromContext.color}`);
          return {
            color: collectionFromContext.color || "#6B7789",
            name: collectionFromContext.name
          };
        }
        
        const data = await fetchCollectionDetailsFromDb(effectiveCollectionId);

        if (data) {
          console.log(`[COLOR DEBUG] Found collection in DB: ${effectiveCollectionId}, color: ${data.color}`);
          return {
            color: data.color || "#6B7789",
            name: data.name
          };
        }
        
        return null;
      } catch (error) {
        console.error("Error in fetchCollectionDetails:", error);
        return null;
      }
    },
    enabled: !!effectiveCollectionId && !!user
  });

  useEffect(() => {
    if (collectionDetails) {
      console.log(`[COLOR DEBUG] Setting collection color to ${collectionDetails.color}`);
      setCollectionColor(collectionDetails.color);
      setCollectionName(`From ${collectionDetails.name}`);
    } else if (effectiveCollectionId) {
      console.log(`[COLOR DEBUG] No collection details found for ${effectiveCollectionId}, using default color #6B7789`);
      setCollectionColor("#6B7789");
      setCollectionName("Related Content");
    } else {
      console.log(`[COLOR DEBUG] No collection ID available, using default color #6B7789`);
      setCollectionColor("#6B7789");
      setCollectionName("Related Content");
    }
  }, [collectionDetails, effectiveCollectionId]);

  const { data: contentItems = [], isLoading, refetch } = useQuery({
    queryKey: ["relatedContent", effectiveCollectionId, currentContentId, resolvedSourceType],
    queryFn: async () => {
      if (!user) {
        return [];
      }

      try {
        // Always prioritize showing content from the same collection
        if (effectiveCollectionId) {
          const items = await fetchContentFromCollection(effectiveCollectionId, user.id, currentContentId || '');
          
          const collectionColorValue = collectionDetails?.color || "#6B7789";
          console.log(`[COLOR DEBUG] Assigning collection color to ${items.length} items: ${collectionColorValue}`);
          
          // Make sure each item has the collection color
          return items.map(item => ({
            ...item,
            collection_color: collectionColorValue,
            collection_id: effectiveCollectionId
          }));
        }
        
        // Fallback: show all related content
        if (resolvedSourceType === "content" && currentContentId) {
          const fetchedItems = await fetchAllRelatedContent(user.id, currentContentId);
          
          const itemsWithDetails = await Promise.all(
            fetchedItems.map(item => fetchItemCollectionDetails(item, getCollectionById))
          );
          
          return itemsWithDetails;
        }
        
        return [];
      } catch (error) {
        console.error("Error in fetchRelatedContent:", error);
        return [];
      }
    },
    enabled: !!user && !!effectiveCollectionId,
    refetchOnMount: true
  });

  // Log content items to help debug the color issue
  useEffect(() => {
    if (contentItems.length > 0) {
      console.log(`[COLOR DEBUG] Related content items:`, {
        count: contentItems.length,
        firstItem: {
          id: contentItems[0].id,
          title: contentItems[0].title,
          color: contentItems[0].collection_color
        },
        currentCollectionColor: collectionColor
      });
    }
  }, [contentItems, collectionColor]);

  return {
    contentItems,
    isLoading,
    collectionColor,
    collectionName,
    refetch
  };
};
